import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { MDDropDownComponent, MDIconComponent, MDHeaderComponent, MDContentBoxComponent, MDTagComponent } from '@melodic-ui/components';
import { LessonStatus } from '@lb/api/mylb/enums';
import { LessonDetailStateService } from '../lesson-detail-state.service';
import { LessonDetailFormStateService } from '../lesson-detail-form-state.service';

@Component({
	selector: 'lb-lesson-detail-objectives',
	templateUrl: './lesson-detail-objectives.component.html',
	styleUrl: './lesson-detail-objectives.component.scss',
	imports: [CommonModule, MDIconComponent, MDHeaderComponent, MDDropDownComponent, MDContentBoxComponent, MDTagComponent],
	standalone: true
})
export class LessonDetailObjectivesComponent {
	readonly state = inject(LessonDetailStateService);
	private readonly formState = inject(LessonDetailFormStateService);

	lesson = this.state.lessonRequired;
	isReadOnly = this.formState.isReadOnly;
	fieldsDisabled = this.formState.fieldsDisabled;
	isLearnerExperience = computed(() => this.state.experience() === 'learner');
	hasCourseCatalog = this.state.hasCourseCatalog;

	readonly objectiveOptions = this.state.objectiveOptions;
	readonly selectedObjectives = this.formState.selectedObjectives;
	readonly savedObjectives = this.formState.savedObjectives;

	isObjectivesInvalid = () => this.formState.isObjectivesInvalid();

	readonly savedObjectiveLabels = computed(() => {
		const saved = this.savedObjectives();
		const options = this.objectiveOptions();
		return options.filter((o) => saved.has(o.value as number));
	});

	/**
	 * A completed lesson with no persisted objectives. Objectives are required to complete a lesson, so an
	 * empty completed lesson means they were cleared after the fact — which only happens when a program
	 * (language/level) change invalidates the objectives scoped to the old language/level.
	 *
	 * Gated to the instructor/admin experience: it's a data-integrity notice for the people who manage the
	 * lesson, not something a learner needs to see about their own completed lesson.
	 */
	readonly objectivesRemovedByProgramChange = computed(
		() => this.state.experience() === 'instructor' && this.state.lesson()?.lessonStatusID === LessonStatus.Completed && this.savedObjectives().size === 0
	);

	onObjectiveChange(value: unknown): void {
		if (Array.isArray(value)) {
			this.formState.updateSelectedObjectives(value);
		}
	}
}
